import { useEffect, useState } from "react";
import {
  useGetSummaryQuery,
} from "../../../app/globalApi";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import useTranslator from "../../../hooks/useTranslator";
import { formatNumber, getCurrentDate } from "../../../helpers/Utils";
import DashboardSection from "../../../features/dashboard/components/DashboardSection";
import DashboardCard from "../../../features/dashboard/components/DashboardCard";
import "../../dashboard/components/dashboard-sections.css";
import "./ReceivingDashboard.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { SnowflakeIconSvg } from "../../../svgFiles/SnowflakeIconSvg";
import { OrangeIconSvg } from "../../../svgFiles/OrangeIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import ColdRoomDataOverview from "../../dashboard/components/ColdRoomDataOverview";
import { useGetReceivingOverviewQuery } from "../receivingApi";

const MetricBox = ({ label, value, unit, color }) => (
  <div className="db-evap-box">
    <div className="db-evap-label" style={color ? { color } : {}}>{label}</div>
    <div className="db-evap-value" style={color ? { color } : {}}>
      {value ?? "0"}{unit && <span className="db-metric-unit"> {unit}</span>}
    </div>
  </div>
);

const ReceivingDashboard = () => {
  const { _t } = useTranslator();  
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const tab = "dashboard";

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
  };


  //  ################## RECEIVING OVERVIEW DATA ##################
  
  const {
    data : overviewData ,
    isLoading : overviewLoading ,
    isFetching : overviewFetching ,
    isError : overviewError ,
    refetch : receivingDataRefetch
  } = useGetReceivingOverviewQuery({startDate : startDate , endDate : endDate});


  useEffect(() => {
    receivingDataRefetch();
  }, []);

  const casesData = overviewData?.data?.cases  ?? [];
  const orangeCases = overviewData?.data?.orange_cases ?? [];
  const weightData = overviewData?.data?.weight_data ?? [];

  // Receiving Overview Items - Orange Cases
  const orangeCasesItems = [
    {
      label: _t("total_orange_cases", "Total Orange Cases"),
      value: orangeCases?.total_orange_cases || 0,
    },
    {
      label: _t("orange_case_increase", "Orange Case Increase"),
      value: orangeCases?.orange_case_increase
        ? Number(orangeCases?.orange_case_increase).toFixed(2) + " %"
        : "0.0 %",
    },
  ];

  // Receiving Overview Items - Weight
  const weightItems = [
    {
      label: _t("kg_entered", "KG Entered"),
      value: formatNumber(weightData?.total_kg_entered ?? 0) || 0,
      unit: " KG",
    },
    {
      label: _t("kg_accepted", "KG Accepted"),
      value: formatNumber(weightData?.total_accepted_kg ?? 0) || 0,
      unit: " KG",
    },
    {
      label: _t("kg_rejected", "KG Rejected"),
      value: formatNumber(weightData?.total_rejected_kg ?? 0) || 0,
      unit: " KG",
    },
    {
      label: _t("kg_per_case", "KG / Case"),
      value: formatNumber(weightData?.kg_per_case ?? 0) || 0,
      unit: " KG",
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="receiving_dashboard_toolbar align-items-center">
        <h5 className="receiving_dashboard_toolbar_title m-0">
          {_t("receiving_overview_title", "Receiving Overview")}
        </h5>
        <div className="receiving_filter_group">          
          <DateRangeFilter setDate={setDate} onApplyRange={handleApplyRange} triggerId="receiving-date-range-filter" />          
        </div>
      </div>

      <div>
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><PackageIconSvg size={18} color="#60a5fa" />{_t("receiving_data", "Receiving Data")}</span>}>
          <div className="receiving-grid-layout">
            <DashboardCard title={_t("cases", "Cases")} icon={<PackageIconSvg size={16} color="#667085" />}>
              <div className="db-evap-row">
                <MetricBox
                  label={_t("cases_entered", "CASES ENTERED")}
                  value={casesData?.cases_entered?.toLocaleString() ?? 0}
                  color="#ee7225"
                />
                <MetricBox
                  label={_t("total_accepted_cases", "CASES ACCEPTED")}
                  value={casesData?.total_accepted_cases?.toLocaleString() ?? 0}
                  color="#25aa91"
                />
                <MetricBox
                  label={_t("cases_rejected", "CASES REJECTED")}
                  value={casesData?.rejected_case_count?.toLocaleString() ?? 0}
                  color="#e53935"
                />
                <MetricBox
                  label={_t("cases_paid", "CASES PAID")}
                  value={casesData?.total_paid_cases?.toLocaleString() ?? 0}
                  color="#25aa91"
                />
              </div>
            </DashboardCard>

            <div className="receiving-bottom-row">
              <DashboardCard title={_t("orange_cases", "Orange Cases")} icon={<OrangeIconSvg size={16} color="#667085" />}>
                <div className="db-evap-row">
                  {orangeCasesItems.map((item, i) => (
                    <div key={i} className="db-evap-box">
                      <div className="db-evap-label">{item.label}</div>
                      <div className="db-evap-value neutral">{item.value}{item.unit || ''}</div>
                    </div>
                  ))}
                </div>
              </DashboardCard>
              
              <DashboardCard title={_t("weight_data", "Weight Data")} icon={<ScaleIconSvg size={16} color="#667085" />}>
                <div className="db-evap-row">
                  {weightItems.map((item, i) => (
                    <div key={i} className="db-evap-box">
                      <div className="db-evap-label">{item.label}</div>
                      <div className="db-evap-value neutral">{item.value}{item.unit || ''}</div>
                    </div>
                  ))}
                </div>
              </DashboardCard>
            </div>
          </div>
        </DashboardSection>

        {/* ── Cold + Room Data Overview ──────────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><SnowflakeIconSvg size={18} color="#60a5fa" />{_t("cold_room_data_overview", "Cold + Room Data Overview")}</span>}>
          <ColdRoomDataOverview />
        </DashboardSection>
      </div>
    </div>
  );
};

export default ReceivingDashboard;